import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, SettingsItemTextField } from '@elipssolution/harfang';
import { styled, Typography } from '@mui/material';
import { ReactNode, SetStateAction, useCallback, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import {
	CHECK_CUSTOMER_FILE_AVAILABILITY_BY_CODE,
	CheckCustomerFileAvailabilityByCodeType,
	FetchSettingsCustomerFileResultType,
	RESTORE_CUSTOMER_FILE_BY_CODE,
	RestoreCustomerFileByCodeType,
} from '../../../api/customerFile';
import { CustomerFileFormType } from '../../../types/settingsCustomerFile';

const ClosedCustomerFileTooltipDiv = styled('div')(({ theme: { spacing } }) => ({
	display: 'flex',
	flexDirection: 'column',
	padding: spacing(1),

	'& > div': {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: spacing(1),
		marginTop: spacing(2),
	},
}));

type ClosedCustomerFileTooltipProps = {
	closedCustomerFileCode: string;
	onTooltipContentSet: (tooltipContent: SetStateAction<ReactNode>) => void;
	onCustomerFileCodeSet: (customerFileCode: string) => Promise<void>;
};

const ClosedCustomerFileTooltip = ({
	closedCustomerFileCode,
	onTooltipContentSet,
	onCustomerFileCodeSet,
}: ClosedCustomerFileTooltipProps) => {
	const closeTooltip = () => onTooltipContentSet(undefined);

	const applyTooltipActions = async () => {
		await onCustomerFileCodeSet(closedCustomerFileCode);
		closeTooltip();
	};

	return (
		<ClosedCustomerFileTooltipDiv>
			<Typography>
				Un dossier client portant le même code ({closedCustomerFileCode}) a été trouvé dans les dossiers clients
				supprimés.
			</Typography>
			<Typography>Voulez-vous le restaurer ?</Typography>

			<div>
				<Button onClick={closeTooltip}>Annuler</Button>
				<Button onClick={applyTooltipActions} variant="contained">
					Restaurer
				</Button>
			</div>
		</ClosedCustomerFileTooltipDiv>
	);
};

let availabilityTimer: ReturnType<typeof setTimeout>;

type CustomerFileCodeFieldProps = {
	control: Control<CustomerFileFormType>;
	disabled: boolean;
	initialValue?: string;
	onCustomerFileChange: (customerFile: Pick<FetchSettingsCustomerFileResultType['customerFile'], 'id'>) => void;
};

const CustomerFileCodeField = ({
	control,
	disabled,
	initialValue,
	onCustomerFileChange,
}: CustomerFileCodeFieldProps) => {
	const [codeFieldTooltipContent, setCodeFieldTooltipContent] = useState<ReactNode>();

	const [checkCustomerFileAvailabilityByCode] = useLazyQuery<CheckCustomerFileAvailabilityByCodeType>(
		CHECK_CUSTOMER_FILE_AVAILABILITY_BY_CODE,
	);

	const [restoreCustomerFileByCode] = useMutation<RestoreCustomerFileByCodeType>(RESTORE_CUSTOMER_FILE_BY_CODE);

	const handleRestoreCustomerFile = useCallback(
		async (customerFileCode: string) => {
			const { data } = await restoreCustomerFileByCode({
				variables: {
					code: customerFileCode,
				},
			});

			const { restoreCustomerFileByCode: restoreCustomerFileByCodeData } = data || {};
			const { id } = restoreCustomerFileByCodeData || {};
			id && onCustomerFileChange({ id });
		},
		[onCustomerFileChange, restoreCustomerFileByCode],
	);

	const checkCodeAvailability = useCallback(
		(customerFileCode: string): Promise<boolean | string> =>
			new Promise((resolve, reject) => {
				clearTimeout(availabilityTimer);

				if (initialValue === customerFileCode) {
					resolve(true);

					return;
				}

				availabilityTimer = setTimeout(() => {
					checkCustomerFileAvailabilityByCode({ variables: { code: customerFileCode } })
						.then(({ data }) => {
							const { checkCustomerFileAvailabilityByCode: checkCustomerFileAvailabilityByCodeData } = data || {};
							const { isAvailable, hasBeenClosed } = checkCustomerFileAvailabilityByCodeData || {};

							const isAvailableAndNotClosed = isAvailable && !hasBeenClosed;
							const isNotAvailableButClosed = !isAvailable && hasBeenClosed;

							if (isNotAvailableButClosed) {
								setCodeFieldTooltipContent(
									<ClosedCustomerFileTooltip
										closedCustomerFileCode={customerFileCode}
										onTooltipContentSet={setCodeFieldTooltipContent}
										onCustomerFileCodeSet={handleRestoreCustomerFile}
									/>,
								);
							}

							resolve(isAvailableAndNotClosed || isNotAvailableButClosed || 'Code dossier déjà utilisé');
						})
						.catch(reject);
				}, 300);
			}),
		[checkCustomerFileAvailabilityByCode, handleRestoreCustomerFile, initialValue],
	);

	return (
		<Controller
			control={control}
			name="code"
			render={({ field: { onChange, ...field }, fieldState: { error } }) => {
				const handleChange = (value?: string) => {
					onChange(value);

					if (codeFieldTooltipContent) {
						setCodeFieldTooltipContent(undefined);
					}
				};

				return (
					<SettingsItemTextField
						{...field}
						description="Code du dossier client."
						disabled={disabled}
						helperText={error?.message ?? ' '}
						invalid={!!error?.message}
						tooltipContent={codeFieldTooltipContent}
						onChange={handleChange}
						label="Code"
						required
					/>
				);
			}}
			rules={{
				required: 'Champ requis',
				pattern: { message: "Le code dossier ne doit pas contenir d'espaces", value: /^\S+$/ },
				validate: checkCodeAvailability,
			}}
		/>
	);
};

export default CustomerFileCodeField;
